/*
  PrivacyPortal device picker - 2026-09-18. Companion to snippets/pp-device-picker.liquid.

  On every change it re-renders the picker's own section through the Section Rendering API with the
  `option_values` parameter, so Shopify resolves the variant server-side and returns fresh markup
  (price, availability, media, option availability) without ever loading all variants into the page.
  Everything Dawn's VariantSelects used to sync (hidden id input, price block, buy button, media,
  URL, share link, pickup availability, the variant-change pub/sub event) is synced here.

  Not every service covers every device (LineageOS does not do a Pixel 11), so after a change the
  old selection may no longer be a real variant. Liquid answers a PARTIAL option_values set with no
  variant at all, so the fallback cannot just drop an option and re-render: it asks the server which
  devices and add-ons the chosen service offers, then re-requests a complete, valid triple.
*/
if (!customElements.get('pp-device-picker')) {
  class PPDevicePicker extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onCoverageInput = this.onCoverageInput.bind(this);
      this.controller = null;
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onCoverageInput);
      this.decorate();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onCoverageInput);
    }

    onCoverageInput(event) {
      if (event.target.matches('[data-coverage-search]')) this.filterCoverage();
    }

    filterCoverage() {
      const search = this.querySelector('[data-coverage-search]');
      if (!search) return;
      const normalize = value => value.toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
      const tokens = normalize(search.value).trim().split(/\s+/).filter(Boolean);
      let matches = 0;
      this.querySelectorAll('[data-coverage-brand]').forEach(group => {
        let count = 0;
        group.querySelectorAll('[data-coverage-device]').forEach(item => {
          const name = normalize(item.dataset.name || item.textContent);
          const visible = tokens.every(token => name.includes(token));
          item.hidden = !visible;
          if (visible) count++;
        });
        group.hidden = count === 0;
        group.open = tokens.length > 0 && count > 0;
        const counter = group.querySelector('[data-brand-count]');
        if (counter) counter.textContent = String(count);
        matches += count;
      });
      const status = this.querySelector('[data-coverage-status]');
      if (status) status.textContent = String(matches) + ' ' + (tokens.length ? status.dataset.matches : status.dataset.all);
      const empty = this.querySelector('[data-coverage-empty]');
      if (empty) empty.hidden = matches > 0;
    }

    get sectionId() {
      return this.dataset.section;
    }

    formatMoney(cents) {
      const amount = Number(cents);
      if (!isFinite(amount)) return '';
      const whole = amount % 100 === 0;
      const decimals = whole ? 0 : 2;
      const dot = (amount / 100).toFixed(decimals);
      const comma = dot.replace('.', ',');
      const format = this.dataset.moneyFormat || '£{{amount}}';
      return format
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, (amount / 100).toFixed(0))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, (amount / 100).toFixed(0))
        .replace(/\{\{\s*amount\s*\}\}/g, dot)
        .replace(/<[^>]*>/g, '')
        .trim();
    }

    selection() {
      const value = (role) => {
        const el = this.querySelector(`[data-role="${role}"]`);
        return el ? el.value : '';
      };
      return { service: value('service'), device: value('device'), addon: value('addon') };
    }

    onChange(event) {
      const role = event.target && event.target.dataset ? event.target.dataset.role : null;
      if (!role) return;
      this.resolve();
    }

    async resolve() {
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setAttribute('aria-busy', 'true');
      try {
        const s = this.selection();
        // The happy path: what is on screen is already a real combination.
        let result = await this.fetchSection([s.service, s.device, s.addon], controller);
        if (!result || !result.fresh.dataset.variant) {
          // It is not, so let the server narrow the lists down to the chosen service and keep as
          // much of the old selection as that service still supports.
          const narrowed = await this.fetchSection([s.service], controller);
          if (narrowed) {
            const device = this.nearest(narrowed.fresh, 'device', s.device);
            const addon = this.nearest(narrowed.fresh, 'addon', s.addon);
            result = await this.fetchSection([s.service, device, addon], controller);
          }
        }
        if (result && result.fresh.dataset.variant) this.apply(result.fresh, result.html);
        else this.markUnavailable();
      } catch (error) {
        if (error.name !== 'AbortError') console.error('pp-device-picker', error);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.removeAttribute('aria-busy');
        }
      }
    }

    // Re-render this section for a set of option values. Built from the current location so that
    // preview keys, theme previews and page-hosted sections all keep working; `variant` is dropped
    // because option_values is the authority on what is selected.
    async fetchSection(values, controller) {
      const ids = values.filter(Boolean);
      if (!ids.length) return null;
      const url = new URL(window.location.href);
      ['variant', 'section_id', 'option_values'].forEach((key) => url.searchParams.delete(key));
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('option_values', ids.join(','));
      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) return null;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.getElementById(this.id);
      return fresh ? { fresh, html } : null;
    }

    // Keep `preferred` if the freshly rendered markup still offers it, otherwise take the first
    // value it does offer.
    nearest(fresh, role, preferred) {
      const select = fresh.querySelector(`[data-role="${role}"]`);
      if (!select) return '';
      if (preferred && select.querySelector(`option[value="${preferred}"]`)) return preferred;
      const first = select.querySelector('option');
      return first ? first.value : '';
    }

    apply(fresh, html) {
      const sectionId = this.sectionId;
      this.innerHTML = fresh.innerHTML;
      ['variant', 'available', 'media', 'price'].forEach((key) => {
        this.dataset[key] = fresh.dataset[key] || '';
      });
      this.decorate();

      const variantId = this.dataset.variant;
      const available = this.dataset.available === 'true';

      // Hidden variant inputs: main form and the installments form.
      document
        .querySelectorAll(`#product-form-${sectionId}, #product-form-installment-${sectionId}`)
        .forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

      // Price / SKU / inventory / volume-pricing blocks, exactly as Dawn syncs them.
      ['price-', 'Sku-', 'Inventory-', 'Volume-', 'Price-Per-Item-'].forEach((prefix) => {
        const source = html.getElementById(prefix + sectionId);
        const destination = document.getElementById(prefix + sectionId);
        if (!source || !destination) return;
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
      });
      const price = document.getElementById(`price-${sectionId}`);
      if (price) price.classList.remove('visibility-hidden');

      // PrivacyPortal stock / delivery lines sit outside the price block.
      const liveRoot = document.getElementById(`shopify-section-${sectionId}`);
      const freshRoot = html.getElementById(`shopify-section-${sectionId}`);
      if (liveRoot && freshRoot) {
        ['.pp-stock', '.pp-delivery'].forEach((selector) => {
          const target = liveRoot.querySelector(selector);
          const replacement = freshRoot.querySelector(selector);
          if (target && replacement) {
            target.className = replacement.className;
            target.textContent = replacement.textContent;
          }
        });
      }

      // Add-to-cart button, Buy it now and any earlier error message.
      const form = document.getElementById(`product-form-${sectionId}`);
      const freshButton = html.getElementById(`ProductSubmitButton-${sectionId}`);
      if (form) {
        const button = form.querySelector('[name="add"]');
        const text = button ? button.querySelector('span') : null;
        const disabled = freshButton ? freshButton.hasAttribute('disabled') : true;
        const strings = window.variantStrings || {};
        if (button) {
          if (disabled) {
            button.setAttribute('disabled', 'disabled');
            if (text) text.textContent = strings.soldOut || 'Sold out';
          } else {
            button.removeAttribute('disabled');
            if (text) text.textContent = strings.addToCart || 'Add to cart';
          }
        }
        const buyNow = form.querySelector('.pp-bin');
        if (buyNow) buyNow.hidden = disabled;
        const productForm = form.closest('product-form');
        if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage();
      }

      // Media: follow the variant's featured media, as Dawn does.
      const mediaId = this.dataset.media;
      if (mediaId) {
        document.querySelectorAll(`[id^="MediaGallery-${sectionId}"]`).forEach((gallery) => {
          const target = `${sectionId}-${mediaId}`;
          if (gallery.setActiveMedia && gallery.querySelector(`[data-media-id="${target}"]`)) {
            gallery.setActiveMedia(target, true);
          }
        });
        const modal = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
        if (modal) {
          const item = modal.querySelector(`[data-media-id="${mediaId}"]`);
          if (item) modal.prepend(item);
        }
      }

      // URL, share link, pickup availability. The address bar is only rewritten on the product's
      // own page: on a preview link or a page-hosted section it carries keys the next section
      // request still needs.
      const onProductPage = window.location.pathname === this.dataset.url;
      if (this.dataset.updateUrl !== 'false' && onProductPage) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variantId}`);
      }
      const share = document.getElementById(`Share-${sectionId}`);
      if (share && share.updateUrl) share.updateUrl(`${window.shopUrl || ''}${this.dataset.url}?variant=${variantId}`);
      const pickup = document.querySelector('pickup-availability');
      if (pickup) {
        if (available && pickup.fetchAvailability) {
          pickup.fetchAvailability(variantId);
        } else {
          pickup.removeAttribute('available');
          pickup.innerHTML = '';
        }
      }

      // Let the rest of the theme know (quantity rules subscribe to this; the sticky buy bar
      // watches the price block and button through MutationObservers and needs nothing).
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId, html, variant: { id: Number(variantId), available } },
        });
      }
    }

    markUnavailable() {
      const sectionId = this.sectionId;
      const form = document.getElementById(`product-form-${sectionId}`);
      const strings = window.variantStrings || {};
      if (form) {
        const button = form.querySelector('[name="add"]');
        const text = button ? button.querySelector('span') : null;
        if (button) button.setAttribute('disabled', 'disabled');
        if (text) text.textContent = strings.unavailable || 'Unavailable';
        const buyNow = form.querySelector('.pp-bin');
        if (buyNow) buyNow.hidden = true;
      }
      ['price-', 'Inventory-', 'Sku-', 'Price-Per-Item-'].forEach((prefix) => {
        const element = document.getElementById(prefix + sectionId);
        if (element) element.classList.add('visibility-hidden');
      });
    }

    // Shopify owns the stored order of an option's values and appends new ones to the end, so the
    // device list arrives with the brands interleaved. Sort it by assets/pp-device-order.js and
    // rebuild the <optgroup>s, which also keeps a device added months from now in its right place.
    reorderDevices(select) {
      const order = window.PP_DEVICE_ORDER;
      if (!Array.isArray(order) || !order.length) return;
      if (!PPDevicePicker.rank) {
        PPDevicePicker.rank = new Map(order.map((name, i) => [name, i]));
      }
      const rank = PPDevicePicker.rank;
      const label = (option) => option.dataset.label || option.textContent;
      const options = Array.from(select.querySelectorAll('option'));
      const sorted = options.slice().sort((a, b) => {
        const ra = rank.has(label(a)) ? rank.get(label(a)) : Number.MAX_SAFE_INTEGER;
        const rb = rank.has(label(b)) ? rank.get(label(b)) : Number.MAX_SAFE_INTEGER;
        return ra - rb || label(a).localeCompare(label(b));
      });
      if (sorted.every((option, i) => option === options[i])) return;

      const selected = select.value;
      select.textContent = '';
      let group = null;
      let currentBrand = null;
      sorted.forEach((option) => {
        const first = label(option).split(' ')[0];
        const brand = first === 'Any' || first === 'Other' ? 'Other' : first;
        if (brand !== currentBrand) {
          currentBrand = brand;
          group = document.createElement('optgroup');
          group.label = brand;
          select.appendChild(group);
        }
        group.appendChild(option);
      });
      select.value = selected;
    }

    // Purely cosmetic: per-device prices in the device dropdown when they differ, "+£27" deltas in
    // the add-on dropdown, the supported-device count and a one-line summary of the selection.
    decorate() {
      this.filterCoverage();
      const money = (cents) => this.formatMoney(cents);
      const chosenLabel = (select) => {
        const option = select && select.selectedOptions && select.selectedOptions[0];
        return option ? option.dataset.label : '';
      };

      // Device: put the list in brand order and append the price only when it actually varies.
      const device = this.querySelector('[data-role="device"]');
      if (device) {
        this.reorderDevices(device);
        const options = Array.from(device.options);
        const prices = options.map((o) => Number(o.dataset.price)).filter((n) => isFinite(n) && n > 0);
        const varies = prices.length > 1 && Math.min(...prices) !== Math.max(...prices);
        options.forEach((option) => {
          const p = Number(option.dataset.price);
          option.textContent = varies && p > 0 ? `${option.dataset.label} — ${money(p)}` : option.dataset.label;
        });
        const count = this.querySelector('[data-count]');
        if (count) {
          count.textContent = `${options.length} supported`;
          count.hidden = options.length === 0;
        }
      }

      // Add-on: show what each one adds to the price of the device already chosen.
      const addon = this.querySelector('[data-role="addon"]');
      if (addon) {
        const options = Array.from(addon.options);
        const prices = options.map((o) => Number(o.dataset.price)).filter((n) => isFinite(n) && n > 0);
        const base = prices.length ? Math.min(...prices) : NaN;
        options.forEach((option) => {
          const p = Number(option.dataset.price);
          const extra = isFinite(p) && isFinite(base) && p > base ? ` +${money(p - base)}` : '';
          option.textContent = `${option.dataset.label}${extra}`;
        });
      }

      const summary = this.querySelector('[data-summary]');
      if (summary) {
        const addonLabel = chosenLabel(addon);
        const parts = [chosenLabel(this.querySelector('[data-role="service"]')), chosenLabel(device)];
        if (addonLabel && !/^no add-on$/i.test(addonLabel)) parts.push(addonLabel);
        summary.textContent = parts.filter(Boolean).join(' · ');
      }
    }
  }

  customElements.define('pp-device-picker', PPDevicePicker);
}
